import { useState } from 'react'
import { motion } from 'framer-motion'
import {
  MapPinIcon,
  StarIcon,
  CheckCircleIcon,
  SparklesIcon,
  ArrowTopRightOnSquareIcon,
  PhotoIcon,
  ChatBubbleLeftIcon,
  MagnifyingGlassIcon,
  Cog6ToothIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Badge } from '../components/ui/Badge'
import { clsx } from 'clsx'
import { analyzeBusinessQuery } from '../lib/groq'

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
}

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 },
}

const businessProfile = {
  name: 'IntelCore HQ',
  address: '123 Business Ave, Tech City, TC 12345',
  phone: '+1 (555) 123-4567',
  website: 'https://intelcorehub.sbs',
  category: 'Software Company',
  rating: 4.8,
  reviewCount: 156,
}

const mockReviews = [
  {
    id: 1,
    author: 'John D.',
    rating: 5,
    text: 'Excellent AI-powered analytics platform. Helped us understand our business metrics better and make data-driven decisions.',
    date: '2 days ago',
  },
  {
    id: 2,
    author: 'Sarah M.',
    rating: 4,
    text: 'Great customer support and the marketing automation features are fantastic. Highly recommend for growing businesses.',
    date: '1 week ago',
  },
  {
    id: 3,
    author: 'Mike R.',
    rating: 5,
    text: 'The vulnerability assessment service saved us from a potential security breach. Outstanding security solutions!',
    date: '2 weeks ago',
  },
  {
    id: 4,
    author: 'Emily K.',
    rating: 4,
    text: 'Solid platform with great integrations. The dashboard is intuitive and the AI suggestions are genuinely helpful.',
    date: '3 weeks ago',
  },
  {
    id: 5,
    author: 'David L.',
    rating: 5,
    text: 'Best business intelligence tool we have used. The local SEO optimizer alone was worth the investment.',
    date: '1 month ago',
  },
]

const seoChecklist = [
  { id: 'hours', label: 'Business Hours', description: 'Listed and up to date', completed: true, icon: Cog6ToothIcon },
  { id: 'photos', label: 'Photos', description: '42 photos uploaded', completed: true, icon: PhotoIcon },
  { id: 'description', label: 'Description', description: 'Business bio added', completed: true, icon: ChatBubbleLeftIcon },
  { id: 'categories', label: 'Categories', description: 'Primary and secondary set', completed: false, icon: MagnifyingGlassIcon },
  { id: 'posts', label: 'Posts', description: 'Weekly posts published', completed: false, icon: GlobeAltIcon },
  { id: 'qa', label: 'Q&A', description: 'Questions answered', completed: true, icon: ChatBubbleLeftIcon },
]

const competitors = [
  { name: 'TechSoft Solutions', rating: 4.5, reviews: 312, responseTime: '< 1 hour' },
  { name: 'DataDriven Co.', rating: 4.3, reviews: 198, responseTime: '< 3 hours' },
  { name: 'CloudPeak Inc.', rating: 4.6, reviews: 276, responseTime: '< 2 hours' },
]

export const GoogleMaps = () => {
  const [apiKey, setApiKey] = useState('')
  const [savedKey, setSavedKey] = useState('')
  const [aiSuggestion, setAiSuggestion] = useState('')
  const [aiLoading, setAiLoading] = useState(false)

  const completedItems = seoChecklist.filter((s) => s.completed).length
  const optimizationScore = Math.round((completedItems / seoChecklist.length) * 100)

  const handleSaveKey = () => {
    setSavedKey(apiKey)
  }

  const handleAiOptimize = async () => {
    setAiLoading(true)
    setAiSuggestion('')
    try {
      const result = await analyzeBusinessQuery(
        'Provide local SEO optimization suggestions for my Google Business Profile.',
        `Business: ${businessProfile.name}, Category: ${businessProfile.category}, Rating: ${businessProfile.rating}/5 (${businessProfile.reviewCount} reviews). Incomplete items: ${seoChecklist.filter((s) => !s.completed).map((s) => s.label).join(', ')}.`
      )
      setAiSuggestion(result)
    } catch {
      setAiSuggestion('Unable to generate suggestions at this time. Please try again later.')
    } finally {
      setAiLoading(false)
    }
  }

  const renderStars = (rating: number) =>
    [...Array(5)].map((_, i) => (
      <StarIcon
        key={i}
        className={clsx('w-4 h-4', i < rating ? 'text-yellow-400' : 'text-metallic-300')}
      />
    ))

  return (
    <motion.div
      variants={container}
      initial="hidden"
      animate="show"
      className="space-y-6"
    >
      {/* Header */}
      <motion.div variants={item} className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-metallic-900">Google Maps Integration</h1>
          <p className="text-metallic-500 mt-1">
            Monitor your Google Business Profile, reviews, and local SEO performance.
          </p>
        </div>
      </motion.div>

      {/* API Key Input */}
      <motion.div variants={item}>
        <Card>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-primary-100 rounded-xl flex items-center justify-center">
              <Cog6ToothIcon className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <h3 className="font-semibold text-metallic-900">Google Maps API Key</h3>
              <p className="text-sm text-metallic-500">Enter your API key to display the live map embed.</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Input
              placeholder="AIzaSy..."
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              className="flex-1"
            />
            <Button onClick={handleSaveKey} disabled={!apiKey.trim()}>
              Save Key
            </Button>
          </div>
          {savedKey && (
            <Badge variant="success" dot className="mt-2">
              API key saved
            </Badge>
          )}
        </Card>
      </motion.div>

      {/* Map Display */}
      <motion.div variants={item}>
        <Card className="p-0 overflow-hidden">
          {savedKey ? (
            <iframe
              title="Google Maps"
              src={`https://www.google.com/maps/embed/v1/place?key=${savedKey}&q=${encodeURIComponent(businessProfile.address)}&zoom=15`}
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          ) : (
            <div className="relative h-[400px] bg-gradient-to-br from-metallic-100 to-metallic-200 flex items-center justify-center">
              <div className="absolute inset-0 opacity-20">
                <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                      <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-metallic-400" />
                    </pattern>
                  </defs>
                  <rect width="100%" height="100%" fill="url(#grid)" />
                </svg>
              </div>
              <div className="relative text-center z-10">
                <MapPinIcon className="w-16 h-16 text-metallic-400 mx-auto mb-4" />
                <p className="text-lg font-medium text-metallic-600">No API Key Provided</p>
                <p className="text-sm text-metallic-400 mt-1">Enter a Google Maps API key above to display the map.</p>
              </div>
            </div>
          )}
        </Card>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Business Profile */}
        <motion.div variants={item}>
          <Card>
            <h3 className="text-lg font-semibold text-metallic-900 mb-4">Business Profile</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <MapPinIcon className="w-5 h-5 text-metallic-400" />
                <div>
                  <p className="text-sm text-metallic-400">Business Name</p>
                  <p className="font-medium text-metallic-900">{businessProfile.name}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <MapPinIcon className="w-5 h-5 text-metallic-400" />
                <div>
                  <p className="text-sm text-metallic-400">Address</p>
                  <p className="font-medium text-metallic-900">{businessProfile.address}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <ChatBubbleLeftIcon className="w-5 h-5 text-metallic-400" />
                <div>
                  <p className="text-sm text-metallic-400">Phone</p>
                  <p className="font-medium text-metallic-900">{businessProfile.phone}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <GlobeAltIcon className="w-5 h-5 text-metallic-400" />
                <div>
                  <p className="text-sm text-metallic-400">Website</p>
                  <p className="font-medium text-primary-600">{businessProfile.website}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <MagnifyingGlassIcon className="w-5 h-5 text-metallic-400" />
                <div>
                  <p className="text-sm text-metallic-400">Category</p>
                  <p className="font-medium text-metallic-900">{businessProfile.category}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <StarIcon className="w-5 h-5 text-yellow-400" />
                <div>
                  <p className="text-sm text-metallic-400">Rating</p>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-metallic-900">{businessProfile.rating}</span>
                    <div className="flex">{renderStars(businessProfile.rating)}</div>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <ChatBubbleLeftIcon className="w-5 h-5 text-metallic-400" />
                <div>
                  <p className="text-sm text-metallic-400">Reviews</p>
                  <p className="font-medium text-metallic-900">{businessProfile.reviewCount} reviews</p>
                </div>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Local SEO Checklist */}
        <motion.div variants={item}>
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-metallic-900">Local SEO Checklist</h3>
              <Badge variant={optimizationScore >= 80 ? 'success' : 'warning'} dot>
                {optimizationScore}% complete
              </Badge>
            </div>
            <div className="space-y-2">
              {seoChecklist.map((check) => (
                <div
                  key={check.id}
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-metallic-50 transition-colors"
                >
                  <div
                    className={clsx(
                      'w-6 h-6 rounded-full flex items-center justify-center',
                      check.completed ? 'bg-success-100' : 'bg-metallic-100'
                    )}
                  >
                    {check.completed ? (
                      <CheckCircleIcon className="w-4 h-4 text-success-500" />
                    ) : (
                      <check.icon className="w-3.5 h-3.5 text-metallic-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <span
                      className={clsx(
                        'text-sm font-medium',
                        check.completed ? 'text-metallic-400 line-through' : 'text-metallic-700'
                      )}
                    >
                      {check.label}
                    </span>
                    <p className="text-xs text-metallic-400">{check.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </motion.div>
      </div>

      {/* AI Optimization Suggestions */}
      <motion.div variants={item}>
        <Card>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center">
                <SparklesIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-metallic-900">AI Optimization Suggestions</h3>
                <p className="text-sm text-metallic-500">Powered by AI analysis of your business profile</p>
              </div>
            </div>
            <Button
              leftIcon={<SparklesIcon className="w-4 h-4" />}
              onClick={handleAiOptimize}
              disabled={aiLoading}
            >
              {aiLoading ? 'Analyzing...' : 'Get Suggestions'}
            </Button>
          </div>
          {aiSuggestion ? (
            <div className="p-4 rounded-xl bg-primary-50 border border-primary-100">
              <p className="text-sm text-metallic-700 whitespace-pre-wrap leading-relaxed">{aiSuggestion}</p>
            </div>
          ) : (
            <div className="p-8 rounded-xl bg-metallic-50 text-center">
              <SparklesIcon className="w-8 h-8 text-metallic-300 mx-auto mb-2" />
              <p className="text-sm text-metallic-400">Click "Get Suggestions" to receive AI-powered optimization tips.</p>
            </div>
          )}
        </Card>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Reviews */}
        <motion.div variants={item}>
          <Card>
            <h3 className="text-lg font-semibold text-metallic-900 mb-4">Recent Reviews</h3>
            <div className="space-y-4">
              {mockReviews.map((review) => (
                <div
                  key={review.id}
                  className="p-4 rounded-xl bg-metallic-50 hover:bg-primary-50 transition-colors"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-metallic-900">{review.author}</span>
                      <div className="flex">{renderStars(review.rating)}</div>
                    </div>
                    <span className="text-xs text-metallic-400">{review.date}</span>
                  </div>
                  <p className="text-sm text-metallic-600">{review.text}</p>
                </div>
              ))}
            </div>
          </Card>
        </motion.div>

        {/* Competitor Comparison */}
        <motion.div variants={item}>
          <Card>
            <h3 className="text-lg font-semibold text-metallic-900 mb-4">Competitor Comparison</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-metallic-200">
                    <th className="text-left py-3 px-2 text-metallic-500 font-medium">Competitor</th>
                    <th className="text-center py-3 px-2 text-metallic-500 font-medium">Rating</th>
                    <th className="text-center py-3 px-2 text-metallic-500 font-medium">Reviews</th>
                    <th className="text-center py-3 px-2 text-metallic-500 font-medium">Response Time</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-metallic-100 bg-primary-50">
                    <td className="py-3 px-2 font-medium text-primary-700">{businessProfile.name} (You)</td>
                    <td className="py-3 px-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <StarIcon className="w-4 h-4 text-yellow-400" />
                        <span className="font-medium text-metallic-900">{businessProfile.rating}</span>
                      </div>
                    </td>
                    <td className="py-3 px-2 text-center text-metallic-900">{businessProfile.reviewCount}</td>
                    <td className="py-3 px-2 text-center text-success-600 font-medium">&lt; 30 min</td>
                  </tr>
                  {competitors.map((comp, i) => (
                    <tr key={i} className="border-b border-metallic-100">
                      <td className="py-3 px-2 text-metallic-900">{comp.name}</td>
                      <td className="py-3 px-2 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <StarIcon className="w-4 h-4 text-yellow-400" />
                          <span className="text-metallic-900">{comp.rating}</span>
                        </div>
                      </td>
                      <td className="py-3 px-2 text-center text-metallic-700">{comp.reviews}</td>
                      <td className="py-3 px-2 text-center text-metallic-600">{comp.responseTime}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 pt-4 border-t border-metallic-100 flex items-center gap-2">
              <ArrowTopRightOnSquareIcon className="w-4 h-4 text-metallic-400" />
              <span className="text-xs text-metallic-400">Data is simulated for demonstration purposes.</span>
            </div>
          </Card>
        </motion.div>
      </div>
    </motion.div>
  )
}
